import {
    spotlightCategoryTooltipText,
    removeSpotlightCategoryTooltipText,
} from './Category.locstring.json';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { DirectionalHint, TooltipHost } from 'office-ui-fabric-react/lib/Tooltip';
import { logUsage } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import * as React from 'react';
import {
    CategoryColorValue,
    CategoryType,
    getCategoryColorsForCategory,
    getMasterCategoryList,
} from 'owa-categories';

const styles = require('./Category.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CategoryProps {
    category: string;
    categoryList?: CategoryType[];
    actionSource: string;
    isLastInWell: boolean;
    containerClassName?: string;
    onCategoryClicked?: (
        ev: React.MouseEvent<unknown>,
        category: string,
        actionSource: string
    ) => void;
    isSpotlightCategory?: boolean;
    onRemoveSpotlightCategory?: (ev: React.MouseEvent<unknown>) => void;
}

@observer
export default class Category extends React.Component<CategoryProps, {}> {
    private categoryContainerElement: HTMLElement;

    @computed
    get categoryColor(): CategoryColorValue {
        return getCategoryColorsForCategory(
            this.props.category,
            this.props.categoryList ? this.props.categoryList : getMasterCategoryList(),
            this.props.isSpotlightCategory
        );
    }

    render() {
        const cStyle = {
            backgroundColor: this.categoryColor.primaryColor,
            borderColor: this.categoryColor.secondaryColor,
            color: this.categoryColor.textColor,
            cursor: this.props.onCategoryClicked ? 'pointer' : 'initial',
        };

        const categoryName = this.props.category;
        const containerClassnames = classNames(
            this.props.containerClassName,
            this.props.isLastInWell ? styles.lastCategoryContainer : styles.categoryContainer
        );

        const categoryLabel = <span className={styles.categoryName}>{categoryName}</span>;

        return (
            <div
                ref={r => (this.categoryContainerElement = r)}
                key={categoryName}
                className={containerClassnames}
                style={cStyle}
                // Include title if there's no tooltip shown.
                title={this.props.isSpotlightCategory ? null : categoryName}
                onMouseEnter={this.onMouseEnterCategory}
                onMouseLeave={this.onMouseLeaveCategory}
                onClick={this.onCategoryClicked}>
                {this.props.isSpotlightCategory
                    ? this.renderSpotlightCategory(categoryLabel)
                    : categoryLabel}
            </div>
        );
    }

    private renderSpotlightCategory = (categoryLabel: JSX.Element) => {
        return (
            <>
                <TooltipHost
                    content={loc(spotlightCategoryTooltipText)}
                    directionalHint={DirectionalHint.bottomCenter}>
                    {categoryLabel}
                </TooltipHost>
                <TooltipHost
                    content={loc(removeSpotlightCategoryTooltipText)}
                    directionalHint={DirectionalHint.bottomCenter}
                    styles={{ root: { display: 'flex' } }}>
                    <Icon
                        iconName={ControlIcons.Cancel}
                        className={styles.removeButton}
                        role="button"
                        onClick={this.onRemoveSpotlightCategory}
                        aria-label={loc(removeSpotlightCategoryTooltipText)}
                    />
                </TooltipHost>
            </>
        );
    };

    private onCategoryClicked = (ev: React.MouseEvent<unknown>) => {
        if (this.props.onCategoryClicked) {
            logUsage('Category_Well_Click', [this.props.actionSource]);
            this.props.onCategoryClicked(ev, this.props.category, this.props.actionSource);
        }
    };

    private onMouseEnterCategory = () => {
        // Can remove when Work Item 58915: [Crawl] Build a "search-like"
        // experience for spotlight items is completed.
        if (this.props.onCategoryClicked || this.props.isSpotlightCategory) {
            this.categoryContainerElement.style.borderColor = this.categoryColor.textColor;
        }
    };

    private onMouseLeaveCategory = () => {
        // Can remove when Work Item 58915: [Crawl] Build a "search-like"
        // experience for spotlight items is completed.
        if (this.props.onCategoryClicked || this.props.isSpotlightCategory) {
            this.categoryContainerElement.style.borderColor = this.categoryColor.secondaryColor;
        }
    };

    private onRemoveSpotlightCategory = (ev: React.MouseEvent<unknown>) => {
        ev.stopPropagation();
        this.props.onRemoveSpotlightCategory && this.props.onRemoveSpotlightCategory(ev);
    };
}
